import { Component } from 'react'
import styled from 'styled-components'
import { FiCalendar } from 'react-icons/fi'
import CalendarService from '../../Services/CalendarService'
import CalendarModal from './CalendarModal'
import { LanguageContext } from '../../theme'

export default class CalendarWidget extends Component {
  static contextType = LanguageContext

  constructor (props) {
    super(props)
    this.state = {
      isModalOpen: false,
      badgeCount: 0
    }
  }

  async componentDidMount () {
    await this.updateBadge()

    // Auto-update badge ogni 5 minuti
    this.badgeInterval = setInterval(() => this.updateBadge(), 5 * 60 * 1000)
  }

  componentWillUnmount () {
    if (this.badgeInterval) {
      clearInterval(this.badgeInterval)
    }
  }

  getLastSeenTimestamp = () => {
    try {
      const timestamp = localStorage.getItem('calendar_last_seen')
      return timestamp ? new Date(timestamp) : null
    } catch (error) {
      return null
    }
  }

  setLastSeenTimestamp = () => {
    try {
      localStorage.setItem('calendar_last_seen', new Date().toISOString())
    } catch (error) {
      console.error('Error saving last seen timestamp:', error)
    }
  }

  updateBadge = async () => {
    try {
      // Fetch eventi di oggi
      const now = new Date()
      const startOfDay = new Date(now)
      startOfDay.setHours(0, 0, 0, 0)
      const endOfDay = new Date(now)
      endOfDay.setHours(23, 59, 59, 999)

      const events = await CalendarService.getEvents(
        startOfDay.toISOString(),
        endOfDay.toISOString()
      )

      // Filtra eventi di oggi
      const todayEvents = CalendarService.filterEventsByDate(events, now)

      // Filtra eventi completati dal conteggio (solo pending)
      const pendingEvents = todayEvents.filter(e => !e.isCompleted)

      // Ottieni l'ultimo timestamp di visualizzazione
      const lastSeen = this.getLastSeenTimestamp()

      if (!lastSeen) {
        // Prima volta che apri il calendario, mostra tutti gli eventi pending
        this.setState({ badgeCount: pendingEvents.length })
      } else {
        // Conta solo eventi pending creati dopo l'ultima visualizzazione
        const newEvents = pendingEvents.filter(event => {
          const createdAt = new Date(event.createdAt)
          return createdAt > lastSeen
        })
        this.setState({ badgeCount: newEvents.length })
      }
    } catch (error) {
      console.error('Error updating badge:', error)
      this.setState({ badgeCount: 0 })
    }
  }

  formatTodayDate = () => {
    const today = new Date()
    const language = this.context?.language || 'it'
    const localeMap = { it: 'it-IT', en: 'en-GB', fr: 'fr-FR' }
    const locale = localeMap[language] || 'it-IT'
    return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(today)
  }

  handleOpenModal = () => {
    // Salva il timestamp corrente come "ultimo visto"
    this.setLastSeenTimestamp()
    // Resetta subito il badge
    this.setState({ isModalOpen: true, badgeCount: 0 })
  }

  handleCloseModal = async () => {
    this.setState({ isModalOpen: false })
    // Aggiorna badge alla chiusura modal (potrebbero esserci nuovi eventi)
    await this.updateBadge()
  }

  render () {
    const { isModalOpen, badgeCount } = this.state
    const hasEvents = badgeCount > 0

    return (
      <>
        <CalendarButton onClick={this.handleOpenModal} title='Calendario' $hasEvents={hasEvents}>
          <FiCalendar />
          <DateText>{this.formatTodayDate()}</DateText>
          {hasEvents && <Badge>{badgeCount > 9 ? '9+' : badgeCount}</Badge>}
        </CalendarButton>

        {isModalOpen && (
          <CalendarModal
            onClose={this.handleCloseModal}
            navigate={this.props.navigate}
          />
        )}
      </>
    )
  }
}

const CalendarButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0 0.75rem;
  height: 32px;
  background: transparent;
  border: 1px solid #666;
  color: #666;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
  position: relative;

  svg {
    font-size: 0.875rem;
    flex-shrink: 0;
  }

  &:hover {
    background: #fafafa;
    border-color: #000;
    color: #000;
    transform: translateY(-1px);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  &:active {
    transform: translateY(0);
    box-shadow: none;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.05);
  }

  @media (max-width: 768px) {
    padding: 0 0.5rem;
    height: 28px;
    gap: 0.375rem;

    svg {
      font-size: 0.75rem;
    }
  }
`

const DateText = styled.span`
  font-size: 0.813rem;
  font-weight: 500;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`

const Badge = styled.span`
  position: absolute;
  top: -6px;
  right: -6px;
  background: #DC2626;
  color: white;
  border-radius: 10px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  font-size: 0.625rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  border: 2px solid #ffffff;

  @media (max-width: 768px) {
    top: -4px;
    right: -4px;
    min-width: 16px;
    height: 16px;
    font-size: 0.563rem;
  }
`
